import { getAccountLocalDate } from '@repo/sdk'
import {
  CalendarCheck2,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Loader2,
  Minus,
} from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useLocation } from 'react-router-dom'

import { formatCheckInDate, shiftCheckInDate } from '@/components/check-in/check-in-date'
import { Button } from '@/components/ui/button'
import { useCheckInHistory } from '@/hooks/use-check-in-history'
import { useAccountTimeZone } from '@/hooks/use-account-time-zone'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'

function monthRange(month: string) {
  const from = `${month}-01`
  const end = new Date(`${from}T00:00:00Z`)
  end.setUTCMonth(end.getUTCMonth() + 1, 0)
  return { from, to: `${month}-${end.getUTCDate()}` }
}

function shiftMonth(month: string, offset: number): string {
  const index
    = Number(month.slice(0, 4)) * 12 + Number(month.slice(5)) - 1 + offset
  const bounded = Math.max(12, Math.min(9999 * 12 + 11, index))
  return `${String(Math.floor(bounded / 12)).padStart(4, '0')}-${String((bounded % 12) + 1).padStart(2, '0')}`
}

/** Remount browsing state when the authoritative identity changes. */
export function CheckInCalendar({ scrollReady }: { scrollReady: boolean }) {
  const { actor } = useAuth()
  const calendar = useAccountTimeZone()
  return (
    <AccountCheckInCalendar
      key={actor?.id ?? 'resolving'}
      timeZone={calendar.timeZone}
      scrollReady={scrollReady}
    />
  )
}

function AccountCheckInCalendar({ timeZone, scrollReady }: { timeZone: string, scrollReady: boolean }) {
  const { t, language } = useI18n()
  const { hash, key: locationKey } = useLocation()
  const [accountToday, setAccountToday] = useState<string | null>(null)
  const [view, setView] = useState(() => ({
    month: getAccountLocalDate(new Date(), timeZone).slice(0, 7),
    selectedDate: null as string | null,
    followCurrent: true,
  }))
  const { month } = view
  const range = monthRange(month)
  const history = useCheckInHistory(range)
  const { data } = history
  const serverToday = data?.today
  const today
    = serverToday ?? accountToday ?? getAccountLocalDate(new Date(history.nowMs), timeZone)
  const currentMonth = today.slice(0, 7)
  const isError = history.isError || history.calendar.isError
  const isLoading
    = history.isResolving
      || (history.hasIdentity && history.isPending && !isError)
  const checkedDates = new Set(data?.checkedDates ?? [])
  const defaultDate = month === currentMonth ? today : range.from
  const selectedDate = view.selectedDate ?? (defaultDate <= today ? defaultDate : null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const scrolledLocation = useRef<string | null>(null)
  const pendingFocusDate = useRef<string | null>(null)
  const isRtl = language === 'ar'

  useEffect(() => {
    if (serverToday)
      setAccountToday(serverToday)
  }, [serverToday])

  useEffect(() => {
    if (serverToday && view.followCurrent && month !== currentMonth) {
      setView({ month: currentMonth, selectedDate: null, followCurrent: true })
    }
  }, [currentMonth, month, serverToday, view.followCurrent])

  useEffect(() => {
    if (hash !== '#check-in-history') {
      scrolledLocation.current = null
      return
    }
    if (!scrollReady || isLoading || (!serverToday && !isError) || (view.followCurrent && month !== currentMonth) || scrolledLocation.current === locationKey)
      return
    const frame = window.requestAnimationFrame(() => {
      sectionRef.current?.focus({ preventScroll: true })
      sectionRef.current?.scrollIntoView({ block: 'start' })
      scrolledLocation.current = locationKey
    })
    return () => window.cancelAnimationFrame(frame)
  }, [currentMonth, hash, isError, isLoading, locationKey, month, scrollReady, serverToday, view.followCurrent])

  useEffect(() => {
    if (!isLoading && !isError && pendingFocusDate.current) {
      calendarRef.current
        ?.querySelector<HTMLButtonElement>(
          `[data-date="${pendingFocusDate.current}"]`,
        )
        ?.focus()
      pendingFocusDate.current = null
    }
  }, [isError, isLoading, month])

  const retry = () => {
    if (history.calendar.isError) {
      void history.calendar
        .initialize()
        .then(() => history.refetch())
        .catch(() => {})
    }
    else {
      void history.refetch()
    }
  }
  const changeMonth = (nextMonth: string) => {
    setView({ month: nextMonth, selectedDate: null, followCurrent: false })
  }
  const dateState = (date: string) =>
    checkedDates.has(date)
      ? 'completed'
      : date > today
        ? 'future'
        : !data || date < data.trackingStartedOn
            ? 'untracked'
            : date === today
              ? 'pending'
              : 'missed'
  const stateLabel = (date: string) => {
    const state = dateState(date)
    return t(`checkIn.${state === 'pending' ? 'todayPending' : state}`)
  }
  const handleDateKey = (
    event: KeyboardEvent<HTMLButtonElement>,
    date: string,
  ) => {
    let target: string
    const weekday = (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7
    switch (event.key) {
      case 'ArrowLeft': {
        target = shiftCheckInDate(date, isRtl ? 1 : -1)
        break
      }
      case 'ArrowRight': {
        target = shiftCheckInDate(date, isRtl ? -1 : 1)
        break
      }
      case 'ArrowUp': {
        target = shiftCheckInDate(date, -7)
        break
      }
      case 'ArrowDown': {
        target = shiftCheckInDate(date, 7)
        break
      }
      case 'Home': {
        target = shiftCheckInDate(date, -weekday)
        break
      }
      case 'End': {
        target = shiftCheckInDate(date, 6 - weekday)
        break
      }
      default: {
        return
      }
    }
    event.preventDefault()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(target) || target < '0001-01-01' || target > today)
      return
    if (target.slice(0, 7) === month) {
      calendarRef.current
        ?.querySelector<HTMLButtonElement>(`[data-date="${target}"]`)
        ?.focus()
    }
    else {
      pendingFocusDate.current = target
    }
    setView({
      month: target.slice(0, 7),
      selectedDate: target,
      followCurrent: false,
    })
  }
  const selectedYear = Number(month.slice(0, 4))
  const currentYear = Number(today.slice(0, 4))
  const firstYear = Math.min(
    Number(data?.trackingStartedOn.slice(0, 4) ?? currentYear),
    selectedYear,
  )
  const lastYear = Math.max(currentYear, selectedYear)
  const years = Array.from(
    { length: lastYear - firstYear + 1 },
    (_, index) => firstYear + index,
  )
  const monthStartWeekday
    = (new Date(`${range.from}T00:00:00Z`).getUTCDay() + 6) % 7
  const daysInMonth = Number(range.to.slice(-2))
  const selectClassName
    = 'h-11 min-w-0 rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:h-9 dark:bg-input/30'

  return (
    <section
      ref={sectionRef}
      id="check-in-history"
      className="scroll-mt-20 border-t pt-5"
      aria-labelledby="check-in-history-title"
      aria-busy={isLoading || undefined}
      dir={isRtl ? 'rtl' : 'ltr'}
      tabIndex={-1}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3
          id="check-in-history-title"
          className="flex items-center gap-2 font-medium"
        >
          <CalendarCheck2 className="size-4 text-primary" aria-hidden="true" />
          {t('checkIn.historyTitle')}
        </h3>
        {!isLoading && !isError && month === currentMonth && (
          <p
            className={cn(
              'flex items-center gap-1.5 text-xs text-muted-foreground',
              checkedDates.has(today) && 'text-primary',
            )}
          >
            {checkedDates.has(today) && (
              <CircleCheck className="size-3.5" aria-hidden="true" />
            )}
            {t(
              checkedDates.has(today)
                ? 'checkIn.completed'
                : 'checkIn.todayPending',
            )}
          </p>
        )}
      </div>

      {isLoading ? (
        <p
          role="status"
          className="flex min-h-20 items-center gap-2 text-sm text-muted-foreground"
        >
          <Loader2
            className="size-4 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          {t('checkIn.loading')}
        </p>
      ) : isError ? (
        <div className="space-y-3 py-5">
          <p role="alert" className="text-sm">
            {t('checkIn.loadError')}
          </p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={retry}
          >
            {t('checkIn.retry')}
          </Button>
        </div>
      ) : data ? (
        <dl className="my-5 flex flex-wrap items-baseline gap-x-6 gap-y-3 text-xs text-muted-foreground">
          <div className="flex w-full items-baseline gap-2 sm:w-auto">
            <dt className="order-last">{t('checkIn.consecutiveDays')}</dt>
            <dd
              className="text-[28px] font-semibold tabular-nums leading-none text-primary"
              data-testid="calendar-current-streak"
            >
              {data.currentStreak}
            </dd>
          </div>
          <div className="flex items-baseline gap-2">
            <dt>{t('checkIn.longestStreak')}</dt>
            <dd
              className="font-medium tabular-nums text-foreground"
              data-testid="calendar-longest-streak"
            >
              {data.longestStreak}
            </dd>
          </div>
          <div className="flex items-baseline gap-2">
            <dt>{t('checkIn.totalDays')}</dt>
            <dd
              className="font-medium tabular-nums text-foreground"
              data-testid="calendar-total-days"
            >
              {data.totalDays}
            </dd>
          </div>
        </dl>
      ) : null}

      <div className="mb-2 flex flex-wrap items-end justify-between gap-x-3 gap-y-1">
        <div className="flex min-w-0 items-end gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11 sm:size-9"
            aria-label={t('checkIn.previousMonth')}
            disabled={month === '0001-01'}
            onClick={() => changeMonth(shiftMonth(month, -1))}
          >
            <ChevronLeft className="size-4 rtl:rotate-180" aria-hidden="true" />
          </Button>
          <label className="grid min-w-0 gap-1 text-xs text-muted-foreground">
            {t('checkIn.year')}
            <select
              aria-label={t('checkIn.year')}
              className={selectClassName}
              value={selectedYear}
              onChange={event =>
                changeMonth(
                  `${event.target.value.padStart(4, '0')}-${month.slice(5)}`,
                )}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
          <label className="grid min-w-0 gap-1 text-xs text-muted-foreground">
            {t('checkIn.month')}
            <select
              aria-label={t('checkIn.month')}
              className={cn(selectClassName, 'max-w-36')}
              value={month.slice(5)}
              onChange={event =>
                changeMonth(`${month.slice(0, 4)}-${event.target.value}`)}
            >
              {Array.from({ length: 12 }, (_, index) => {
                const number = String(index + 1).padStart(2, '0')
                return (
                  <option key={number} value={number}>
                    {formatCheckInDate(`2026-${number}-01`, language, {
                      month: 'long',
                    })}
                  </option>
                )
              })}
            </select>
          </label>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11 sm:size-9"
            aria-label={t('checkIn.nextMonth')}
            disabled={month === '9999-12'}
            onClick={() => changeMonth(shiftMonth(month, 1))}
          >
            <ChevronRight
              className="size-4 rtl:rotate-180"
              aria-hidden="true"
            />
          </Button>
        </div>
        <Button
          type="button"
          variant="link"
          className="h-auto min-h-11 whitespace-normal px-0 text-xs sm:min-h-9"
          onClick={() =>
            setView({
              month: currentMonth,
              selectedDate: null,
              followCurrent: true,
            })}
        >
          {t('checkIn.backToCurrentMonth')}
        </Button>
      </div>

      {!isLoading && !isError && data && (
        <>
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1" aria-hidden="true">
            {Array.from({ length: 7 }, (_, index) => (
              <span
                key={index}
                className="min-w-0 py-2 text-center text-xs text-muted-foreground"
              >
                <abbr
                  className="no-underline"
                  title={formatCheckInDate(
                    shiftCheckInDate('2026-10-19', index),
                    language,
                    { weekday: 'long' },
                  )}
                >
                  {formatCheckInDate(
                    shiftCheckInDate('2026-10-19', index),
                    language,
                    { weekday: isRtl ? 'narrow' : 'short' },
                  )}
                </abbr>
              </span>
            ))}
          </div>
          <div
            ref={calendarRef}
            role="group"
            aria-label={formatCheckInDate(range.from, language, {
              year: 'numeric',
              month: 'long',
            })}
            className="grid grid-cols-7 gap-0.5 sm:gap-1"
          >
            {Array.from({ length: monthStartWeekday }, (_, index) => (
              <span key={`blank-${index}`} aria-hidden="true" />
            ))}
            {Array.from({ length: daysInMonth }, (_, index) => {
              const date = `${month}-${String(index + 1).padStart(2, '0')}`
              const state = dateState(date)
              const isToday = date === today
              return (
                <button
                  key={date}
                  type="button"
                  className={cn(
                    'flex h-11 min-w-0 flex-col items-center justify-center gap-0.5 rounded-md border border-transparent bg-muted/50 p-0.5 text-xs tabular-nums text-muted-foreground transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring enabled:hover:bg-accent disabled:cursor-default sm:h-12 sm:text-sm',
                    state === 'completed' && 'bg-primary/15 text-primary',
                    (state === 'future' || state === 'untracked')
                    && 'bg-transparent text-muted-foreground/70',
                    isToday && 'border-primary text-foreground',
                    date === selectedDate && 'ring-1 ring-inset ring-primary',
                  )}
                  aria-label={`${formatCheckInDate(date, language, { dateStyle: 'full' })}, ${stateLabel(date)}`}
                  aria-current={isToday ? 'date' : undefined}
                  aria-pressed={date === selectedDate}
                  disabled={state === 'future'}
                  data-date={date}
                  data-state={state}
                  onClick={() =>
                    setView(previous => ({ ...previous, selectedDate: date }))}
                  onKeyDown={event => handleDateKey(event, date)}
                >
                  <span aria-hidden="true">{index + 1}</span>
                  {state === 'completed' ? (
                    <Check className="size-3" aria-hidden="true" />
                  ) : isToday ? (
                    <span
                      className="text-[10px] leading-none"
                      aria-hidden="true"
                    >
                      {t('checkIn.today')}
                    </span>
                  ) : state === 'missed' ? (
                    <Minus className="size-2.5" aria-hidden="true" />
                  ) : null}
                </button>
              )
            })}
          </div>
          <p
            role="status"
            className="my-3 min-h-5 text-xs leading-relaxed text-muted-foreground"
          >
            {selectedDate ? (
              <>{formatCheckInDate(selectedDate, language, { dateStyle: 'full' })} · {stateLabel(selectedDate)}</>
            ) : (
              <>{formatCheckInDate(range.from, language, { month: 'long', year: 'numeric' })} · {t('checkIn.future')}</>
            )}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Check className="size-3 text-primary" aria-hidden="true" />
              {t('checkIn.completed')}
            </span>
            <span className="flex items-center gap-1.5">
              <Minus className="size-3" aria-hidden="true" />
              {t('checkIn.missed')}
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="size-3 rounded-sm border border-primary"
                aria-hidden="true"
              />
              {t('checkIn.today')}
            </span>
          </div>
          {data.totalDays === 0 && (
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              {t('checkIn.startPrompt')}
            </p>
          )}
          <p className="mt-4 border-t pt-3 text-xs text-muted-foreground">
            {t('checkIn.timeZone', { timeZone: data.timeZone })}
          </p>
        </>
      )}
    </section>
  )
}
